/**
 * View Switch Group - Figma 778-2268: 칩 형태의 단일 선택 라디오 (연결/별도, 홈 RSB 기능 탭 등)
 *
 * 누른 쪽이 선택되고, 이미 선택된 쪽을 다시 눌러도 변화 없음 —
 * 디자인(7159-490523)의 버튼이 54px 폭이라 원하는 쪽을 직접 누르는 데 무리가 없어 반전 동작을 두지 않는다.
 */

import { cn } from "@/lib/utils";
import { Tooltip } from "@/toggle-ui-kit/tooltip";
import { cva, type VariantProps } from "class-variance-authority";
import { Fragment } from "react";

// ============================================
// 타입 정의
// ============================================

export interface ViewSwitchOption<T extends string = string> {
  value: T;
  label: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  disabled?: boolean;
  /**
   * 보조 설명. disabled 항목의 이유를 알리는 용도 — hover/focus 로 뜬다.
   * 이 값이 있으면 disabled 항목이 `aria-disabled` 로 바뀌어 포커스를 받는다.
   */
  tooltip?: React.ReactNode;
  /** 항목 버튼에 붙일 클래스. 베타 표식(`beta-feature-inset-target`) 등 바깥 표시용. */
  className?: string;
}

type ViewSwitchSize = "24px" | "28px" | "32px" | "36px";

export interface ViewSwitchGroupProps<T extends string = string>
  extends VariantProps<typeof viewSwitchItemVariants> {
  options: ViewSwitchOption<T>[];
  value: T;
  onChange?: (value: T) => void;
  size?: ViewSwitchSize;
  className?: string;
}

// ============================================
// 스타일 (CVA)
// ============================================

const viewContainerVariants = cva(
  "inline-flex items-center gap-0.5 flex-nowrap bg-background-neutral-alternative",
  {
    variants: {
      size: {
        "24px": "p-0.5 rounded-[0.375rem]",
        // Figma 7843-258967 (상단 바): 컨테이너 36px = 패딩 4 + 항목 28, 라운드 8
        "28px": "p-1 rounded-[0.5rem]",
        "32px": "p-0.5 rounded-[0.5rem]",
        // Figma 6826-629009 (홈 RSB 탭): 컨테이너 패딩 4 + 항목 36, 라운드 8
        "36px": "p-1 rounded-[0.5rem]",
      },
    },
    defaultVariants: {
      size: "24px",
    },
  },
);

// Figma 612-10809: 24px switch item — height 24px, padding H 8px, radius 4px, caption-2-medium, selected=white+Shadow/Base
const viewSwitchItemVariants = cva(
  "inline-flex items-center justify-center cursor-pointer transition-colors",
  {
    variants: {
      selected: {
        true: "bg-white shadow-base text-label-normal",
        false: "bg-transparent text-label-alternative",
      },
      size: {
        "24px":
          "h-6 min-h-6 px-2 gap-1 rounded-[0.25rem] caption-2-medium border-0",
        // 상단 바 항목은 70px 고정폭 (연결/별도)
        "28px":
          "h-7 min-w-[70px] px-2 gap-1 rounded-[0.375rem] label-2-medium border-0",
        "32px":
          "h-8 px-2 gap-1 rounded-[0.375rem] label-1-normal-medium border-0",
        "36px":
          "h-9 px-2.5 gap-1 rounded-[0.375rem] body-2-normal-medium border-0",
      },
    },
    defaultVariants: {
      selected: false,
      size: "24px",
    },
  },
);

const iconSizeStyles: Record<ViewSwitchSize, string> = {
  "24px": "size-3.5",
  "28px": "size-4",
  "32px": "size-4",
  "36px": "size-4",
};

// ============================================
// ViewSwitchGroup 컴포넌트
// ============================================

function ViewSwitchGroupInner<T extends string = string>({
  options,
  value,
  onChange,
  size = "24px",
  className,
}: ViewSwitchGroupProps<T>) {
  return (
    <div
      role="radiogroup"
      className={cn(viewContainerVariants({ size }), className)}
    >
      {options.map((option) => {
        const selected = value === option.value;
        const item = (
          <button
            type="button"
            role="radio"
            aria-checked={selected}
            // 이유를 말해 줄 tooltip 이 있으면 native disabled 를 쓰지 않는다 — disabled
            // 버튼은 포커스도 포인터 이벤트도 받지 못해 그 이유가 키보드·스크린리더
            // 사용자에게 닿지 않는다. 동작 차단은 아래 onClick 가드가 이미 맡는다.
            disabled={option.disabled && !option.tooltip}
            aria-disabled={option.disabled || undefined}
            onClick={() => {
              if (!option.disabled && !selected) onChange?.(option.value);
            }}
            className={cn(
              viewSwitchItemVariants({ selected, size }),
              option.disabled && "opacity-50 cursor-not-allowed",
              option.className,
            )}
          >
            {option.leftIcon && (
              <span
                className={cn(
                  "flex items-center justify-center shrink-0",
                  iconSizeStyles[size],
                )}
                aria-hidden
              >
                {option.leftIcon}
              </span>
            )}
            <span className="whitespace-nowrap">{option.label}</span>
            {option.rightIcon && (
              <span
                className={cn(
                  "flex items-center justify-center shrink-0",
                  iconSizeStyles[size],
                )}
                aria-hidden
              >
                {option.rightIcon}
              </span>
            )}
          </button>
        );

        return option.tooltip ? (
          <Tooltip key={option.value} label={option.tooltip} side="bottom">
            {item}
          </Tooltip>
        ) : (
          <Fragment key={option.value}>{item}</Fragment>
        );
      })}
    </div>
  );
}

// 제네릭 + forwardRef 패턴 없이 export (필요 시 추가 가능)
export function ViewSwitchGroup<T extends string = string>(
  props: ViewSwitchGroupProps<T>,
) {
  return <ViewSwitchGroupInner {...props} />;
}
